import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// Async Thunks
export const fetchConnections = createAsyncThunk(
  'adConfig/fetchConnections',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/config/connections');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch connections');
    }
  }
);

export const fetchSettings = createAsyncThunk(
  'adConfig/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/config/settings');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch settings');
    }
  }
);

export const updateSettings = createAsyncThunk(
  'adConfig/updateSettings',
  async (settingsData, { rejectWithValue }) => {
    try {
      const response = await api.put('/marketing/config/settings', settingsData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update settings');
    }
  }
);

export const fetchSummary = createAsyncThunk(
  'adConfig/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/config/summary');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch summary');
    }
  }
);

// Initial State
const initialState = {
  connections: {},
  settings: {
    budget: {
      dailyBudgetCap: null,
      monthlyBudgetCap: null,
      alertThreshold: 80,
      currency: 'USD',
    },
    platforms: {
      enabledPlatforms: ['google', 'meta'],
      platformBudgets: {},
    },
    autoReplyAssignments: [],
    notifications: {
      budgetAlerts: true,
      dailySummary: false,
      alertEmails: [],
    },
  },
  summary: null,
  loading: false,
  error: null,
};

// Slice
const adConfigSlice = createSlice({
  name: 'adConfig',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Connections
    builder.addCase(fetchConnections.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchConnections.fulfilled, (state, action) => {
      state.loading = false;
      state.connections = action.payload.connections || {};
    });
    builder.addCase(fetchConnections.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Settings
    builder.addCase(fetchSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.settings = action.payload.settings || initialState.settings;
    });
    builder.addCase(fetchSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Update Settings
    builder.addCase(updateSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.settings = action.payload.settings || state.settings;
    });
    builder.addCase(updateSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Summary
    builder.addCase(fetchSummary.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchSummary.fulfilled, (state, action) => {
      state.loading = false;
      state.summary = action.payload.summary;
      if (action.payload.summary) {
        state.settings = action.payload.summary.settings || state.settings;
        state.connections = action.payload.summary.connections || {};
      }
    });
    builder.addCase(fetchSummary.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { clearError } = adConfigSlice.actions;
export default adConfigSlice.reducer;
